import React, { useState } from 'react';
import { Button, Table } from 'govuk-react-jsx';
import { useHistory, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  getCustomerName,
  getAddressLine1,
  getAddressLine2,
  getTownOrCity,
  getCounty,
  getPostCode,
  getWhoCompletedInstallation,
  getDateOfCompletion,
  getCostOfInstallation,
  getUploadedFile,
  getTotalGrantAmount,
  getIndividualChargepointDetails,
  setApplicationClaimId,
  getSubcontractorDetails,
  getInstallerName,
  getOzevInstallerNum,
  setChosenChargePoint,
} from '../../features/installer/installerJourneySlice';

import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import { submitClaim } from '../../firebase';
import { getApplicationDocumentId } from '../../features/application/applicationJourneySlice';
import useTitle from '../../hooks/useTitle';

const CheckYourAnswers = () => {
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  const dispatch = useDispatch();
  useTitle(
    'installer.CheckYourAnswers.s28',
    'Check your answers - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const customerNameRedux = useSelector(getCustomerName);
  const addressLine1Redux = useSelector(getAddressLine1);
  const addressLine2Redux = useSelector(getAddressLine2);
  const townOrCityRedux = useSelector(getTownOrCity);
  const countyRedux = useSelector(getCounty);
  const postCodeRedux = useSelector(getPostCode);
  const whoCompletedInstallationRedux = useSelector(
    getWhoCompletedInstallation
  );
  const subcontractorDetailsRedux = useSelector(getSubcontractorDetails);
  const installerNameRedux = useSelector(getInstallerName);
  const individualChargepointDetailsRedux = useSelector(
    getIndividualChargepointDetails
  );
  const dateInstallationsCompletedRedux = useSelector(getDateOfCompletion);
  const totalCostInstallRedux = useSelector(getCostOfInstallation);
  const proofCustomerInvoiceRedux = useSelector(getUploadedFile);
  const totalGrantAmountRedux = useSelector(getTotalGrantAmount);
  const applicationId = useSelector(getApplicationDocumentId);
  const ozevInstallerNum = useSelector(getOzevInstallerNum);
  const history = useHistory();
  const [claimSubmitted, setClaimSubmitted] = useState(false);

  const getInvoiceFilesNames = (invoiceFiles) => {
    return Array.from(invoiceFiles).map((invoiceFile) => {
      return <section key={invoiceFile.name}> {invoiceFile.name}</section>;
    });
  };

  const { chargepointsInstalled, socketsInstalled } =
    individualChargepointDetailsRedux.reduce(
      (acc, chargepoint) => {
        acc.chargepointsInstalled += 1;
        acc.socketsInstalled += chargepoint.socket;

        return acc;
      },
      { chargepointsInstalled: 0, socketsInstalled: 0 }
    );

  const handleBtn = () => {
    // prevent double clicking and multiple submissions
    setClaimSubmitted(true);
    submitClaim({
      applicationId,
    })
      .then((response) => {
        dispatch(setApplicationClaimId(response.data.claimId));
        history.push('/installer/claim-submitted');
      })
      .catch(() => {
        // TODO: Show error
      });
  };

  const setChargepoint = (chargepoint) => {
    dispatch(setChosenChargePoint(chargepoint));
  };

  const address = (
    <>
      <section>{addressLine1Redux && `${addressLine1Redux},`}</section>
      <section>{addressLine2Redux && `${addressLine2Redux},`}</section>
      <section>{townOrCityRedux && `${townOrCityRedux},`}</section>
      <section>{countyRedux && `${countyRedux},`}</section>
      <section>{postCodeRedux && postCodeRedux}</section>
    </>
  );

  const whoCompleted =
    whoCompletedInstallationRedux === 'A subcontractor' ? (
      <>
        {copyProvider.getCopy(
          'installer.CheckYourAnswers.s27',
          'Subcontractor'
        )}
        <br />
        {subcontractorDetailsRedux.subcontractor_name}
      </>
    ) : (
      installerNameRedux
    );

  const installerOzev =
    whoCompletedInstallationRedux === 'A subcontractor'
      ? subcontractorDetailsRedux.subcontractor_number
      : ozevInstallerNum;

  const pageModel = (
    <>
      <h1 className="govuk-heading-xl">
        {copyProvider.getCopy(
          'installer.CheckYourAnswers.s1',
          'Check your answers'
        )}
      </h1>
      <h2 className="govuk-heading-m">
        {copyProvider.getCopy(
          'installer.CheckYourAnswers.s2',
          'Installation details'
        )}
      </h2>
      <Table
        className="govuk-summary-list govuk-!-margin-bottom-9 "
        firstCellIsHeader
        rows={[
          //  Customer name:
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s3',
                  "Customer's name"
                ),
                format: 'text',
              },
              {
                children: customerNameRedux,
                format: 'text',
              },
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s0',
                  ''
                ),
                format: 'text',
              },
            ],
          },
          // Address:
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s4',
                  'Address'
                ),
                format: 'text',
              },
              {
                children: address,
                format: 'text',
              },
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s0',
                  ''
                ),
                format: 'text',
              },
            ],
          },
          // Who completed the installation:
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s5',
                  'Who completed the installation'
                ),
                format: 'text',
              },
              {
                children: whoCompleted,
                format: 'text',
              },

              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/installer/subcontractor',
                      state: {
                        isFromCheckAnswers: true,
                      },
                    }}
                  >
                    {copyProvider.getCopy(
                      'installer.CheckYourAnswers.s6',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
          // Installers OZEV number
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s24',
                  "Installer's OZEV number"
                ),
                format: 'text',
              },
              {
                children: installerOzev,
                format: 'text',
              },

              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/installer/subcontractor',
                      state: { isFromCheckAnswers: true },
                    }}
                  >
                    {copyProvider.getCopy(
                      'installer.CheckYourAnswers.s6',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
          // Date installations were completed:
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s7',
                  'Date you completed the installation'
                ),
                format: 'text',
              },
              {
                children:
                  dateInstallationsCompletedRedux &&
                  dateInstallationsCompletedRedux,
                format: 'text',
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/installer/installation-date',
                      state: { isFromCheckAnswers: true },
                    }}
                  >
                    {copyProvider.getCopy(
                      'installer.CheckYourAnswers.s8',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
        ]}
      />
      {/* Chargepoint details */}
      <h2 className="govuk-heading-m govuk-!-margin-top-9">
        {copyProvider.getCopy(
          'installer.CheckYourAnswers.s9',
          'Chargepoint details'
        )}
      </h2>
      <Table
        captionClassName="govuk-heading-m"
        firstCellIsHeader
        rows={[
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s25',
                  'Number of chargepoints you installed'
                ),
                format: 'text',
                className: 'govuk-summary-list__key',
              },
              {
                children: chargepointsInstalled,
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/installer/chargepoints-summary',
                      state: { isFromCheckAnswers: true },
                    }}
                  >
                    {copyProvider.getCopy(
                      'installer.CheckYourAnswers.s14',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'text',
                className: 'govuk-summary-list__action',
              },
            ],
          },
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s26',
                  'Number of chargepoint sockets you installed'
                ),
                format: 'text',
                className: 'govuk-summary-list__key',
              },
              {
                children: socketsInstalled,
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/installer/additional-chargepoints-details',
                      state: { isFromCheckAnswers: true },
                    }}
                  >
                    {copyProvider.getCopy(
                      'installer.CheckYourAnswers.s14',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'text',
                className: 'govuk-summary-list__action',
              },
            ],
          },
        ].concat(
          individualChargepointDetailsRedux.map((chargepoint, index) => {
            const { manufacturer } = chargepoint;
            const { model } = chargepoint;
            const { serialNumber } = chargepoint;
            const { photos } = chargepoint;
            const { socket } = chargepoint;

            return {
              cells: [
                {
                  children: `${copyProvider.getCopy(
                    'installer.CheckYourAnswers.s10',
                    'Chargepoint'
                  )} ${index + 1}`,
                  format: 'text',
                  className: 'govuk-summary-list__key',
                },
                <br />,
                {
                  children: (
                    <table>
                      <tr>
                        <th>
                          <strong>
                            {copyProvider.getCopy(
                              'installer.CheckYourAnswers.s11',
                              'Manufacturer: '
                            )}
                          </strong>
                        </th>
                        <td>{manufacturer}</td>
                      </tr>

                      <tr>
                        <th>
                          <strong>
                            {copyProvider.getCopy(
                              'installer.CheckYourAnswers.s13',
                              'Model: '
                            )}
                          </strong>
                        </th>
                        <td>{model}</td>
                      </tr>

                      <tr>
                        <th>
                          <strong>
                            {copyProvider.getCopy(
                              'installer.CheckYourAnswers.s29',
                              'Chargepoint sockets: '
                            )}
                          </strong>
                        </th>
                        <td>{socket}</td>
                      </tr>

                      <tr>
                        <th>
                          <strong>
                            {copyProvider.getCopy(
                              'installer.CheckYourAnswers.s30',
                              'Serial number: '
                            )}
                          </strong>
                        </th>
                        <td>{serialNumber}</td>
                      </tr>

                      <tr>
                        <th>
                          <strong>
                            {copyProvider.getCopy(
                              'installer.CheckYourAnswers.s31',
                              'Photos: '
                            )}
                          </strong>
                        </th>
                        <td>
                          {photos.map((photo) => photo.fileName).join(', ')}
                        </td>
                      </tr>
                    </table>
                  ),
                  format: 'text',
                  className: 'govuk-summary-list__value',
                },
                {
                  children: (
                    <Link
                      className="govuk-link"
                      to={{
                        pathname: '/installer/additional-chargepoints-details',
                        state: {
                          isFromCheckAnswers: true,
                          chargepointIndex: index,
                        },
                      }}
                      onClick={() => setChargepoint(chargepoint)}
                    >
                      {copyProvider.getCopy(
                        'installer.CheckYourAnswers.s14',
                        'Change'
                      )}
                    </Link>
                  ),
                  format: 'text',
                  className: 'govuk-summary-list__action',
                },
              ],
            };
          })
        )}
      />
      {/* Financial details */}
      <h2 className="govuk-heading-m govuk-!-margin-top-9">
        {copyProvider.getCopy(
          'installer.CheckYourAnswers.s17',
          'Financial details'
        )}
      </h2>
      {/* Total cost of installation */}
      <Table
        className="govuk-summary-list govuk-!-margin-bottom-9 govuk-grid-column-two-thirds"
        firstCellIsHeader
        rows={[
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s18',
                  'Total cost of installation'
                ),
                format: 'text',
              },
              {
                children: `£${Number(totalCostInstallRedux).toFixed(2)}`,
                format: 'text',
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/installer/installation-cost',
                      state: { isFromCheckAnswers: true },
                    }}
                  >
                    {copyProvider.getCopy(
                      'installer.CheckYourAnswers.s19',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
          // Proof of customer invoice:
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'installer.CheckYourAnswers.s20',
                  'Customer invoice'
                ),
                format: 'text',
              },
              {
                children: (
                  <div>
                    {getInvoiceFilesNames(proofCustomerInvoiceRedux?.files)}
                  </div>
                ),
                format: 'text',
              },
              {
                children: (
                  <Link
                    className="govuk-link"
                    to={{
                      pathname: '/installer/installation-cost',
                      state: { isFromCheckAnswers: true },
                    }}
                  >
                    {copyProvider.getCopy(
                      'installer.CheckYourAnswers.s21',
                      'Change'
                    )}
                  </Link>
                ),
                format: 'numeric',
              },
            ],
          },
          // Total grant amount to receive:
          {
            cells: [
              {
                children: (
                  <strong>
                    {copyProvider.getCopy(
                      'installer.CheckYourAnswers.s22',
                      'Total grant amount'
                    )}
                    <br />
                  </strong>
                ),
                format: 'text',
              },
              {
                children: `£${Number(totalGrantAmountRedux).toFixed(2)}`,
                format: 'text',
              },
              {
                children: '',
                format: 'text',
              },
            ],
          },
        ]}
      />
      {/* Button link */}
      <Button
        className="govuk-button"
        onClick={handleBtn}
        disabled={claimSubmitted}
      >
        {copyProvider.getCopy(
          'installer.CheckYourAnswers.s23',
          'Confirm and submit claim'
        )}
      </Button>
    </>
  );
  return pageModel;
};

export default CheckYourAnswers;
